import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllUsers } from "../slices/allUsersSlice";
import { getUserDetails, updateUser, deleteUser, resetUpdate, resetDelete } from "../slices/userDetailsSlice";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";

const UserManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { activeRoles } = useSelector((state) => state.role);
  const { users, loading } = useSelector((state) => state.allUsers);
  const { isUpdated, isDeleted } = useSelector((state) => state.userDetails);

  usePermissions();
  const hasLoadedUsers = useRef(false);

  // Check permission directly from Redux state to avoid infinite loops
  const canReadUsers = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "users");
    return permission && permission.actions.includes("read");
  }, [user?.role, activeRoles]);

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "admin",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadUsers) {
      navigate("/my-business");
      return;
    }

    // Only load users once when component mounts and user is authenticated
    if (!hasLoadedUsers.current) {
      dispatch(getAllUsers());
      hasLoadedUsers.current = true;
    }
  }, [dispatch, isAuthenticated, navigate, canReadUsers]);

  useEffect(() => {
    if (isUpdated) {
      dispatch(resetUpdate());
      setIsModalOpen(false);
      setFormData({ name: "", email: "", role: "" });
      setEditingUser(null);
      hasLoadedUsers.current = false;
      dispatch(getAllUsers());
      hasLoadedUsers.current = true;
    }
  }, [isUpdated, dispatch]);

  useEffect(() => {
    if (isDeleted) {
      dispatch(resetDelete());
      setIsDeleteModalOpen(false);
      setDeleteUserId(null);
      hasLoadedUsers.current = false;
      dispatch(getAllUsers());
      hasLoadedUsers.current = true;
    }
  }, [isDeleted, dispatch]);

  // eslint-disable-next-line no-unused-vars
  const _handleSearch = (e) => {
    e.preventDefault();
    // Filter users locally based on search term
  };

  const handleOpenModal = async (userData = null) => {
    if (userData) {
      setEditingUser(userData);
      setFormData({
        name: userData.name || "",
        email: userData.email || "",
        role: userData.role || "admin",
      });
    } else {
      setEditingUser(null);
      setFormData({ name: "", email: "", role: "admin" });
    }
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ name: "", email: "", role: "admin" });
    setEditingUser(null);
    setValidationErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.role.trim()) {
      errors.role = "Role is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (editingUser) {
      dispatch(updateUser({
        id: editingUser._id,
        userData: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role.trim(),
        },
      }));
    }
  };

  const handleOpenDeleteModal = (userId) => {
    setDeleteUserId(userId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteUserId(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = () => {
    if (deleteUserId) {
      dispatch(deleteUser(deleteUserId));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _handleView = async (userId) => {
    await dispatch(getUserDetails(userId));
    const userData = users.find((u) => u._id === userId);
    if (userData) {
      handleOpenModal(userData);
    }
  };


  // Filter users based on search term
  const filteredUsers = users.filter((user) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.role?.toLowerCase().includes(search)
    );
  });

  if (loading && users.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{
        ...styles.content,
        padding: isMobile ? "16px" : "24px",
        paddingBottom: isMobile ? "100px" : "100px",
      }}>
        {/* Page Header */}
        <div style={{
          ...styles.pageHeader,
          marginBottom: isMobile ? "16px" : "24px",
        }}>
          <h1 style={{
            ...styles.pageTitle,
            fontSize: isMobile ? "28px" : "32px",
          }}>User List</h1>
        </div>

        {/* Search Bar */}
        <div style={{
          ...styles.searchContainer,
          marginBottom: isMobile ? "16px" : "20px",
          padding: isMobile ? "8px" : "12px",
        }}>
          <div style={{ position: "relative", width: "100%" }}>
            <div style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#667781",
              fontSize: "16px",
              pointerEvents: "none",
              zIndex: 1,
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* User Cards */}
        <div style={{
          ...styles.usersContainer,
          gap: isMobile ? "12px" : "16px",
        }}>
          {filteredUsers.length === 0 ? (
            <div style={{
              ...styles.noUsers,
              padding: isMobile ? "60px 20px" : "80px 40px",
            }}>
              <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.6 }}>👤</div>
              <p style={{ margin: "0 0 24px 0", fontSize: "17px", fontWeight: "400", color: "#667781" }}>
                No users found
              </p>
            </div>
          ) : (
            filteredUsers.map((userItem) => (
              <div 
                key={userItem._id} 
                style={{
                  ...styles.userCard,
                  padding: isMobile ? "16px" : "20px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                }}
              >
                <div style={styles.userCardContent}>
                  <div style={styles.userInfo}>
                    <div style={{
                      ...styles.userName,
                      fontSize: isMobile ? "16px" : "18px",
                    }}>{userItem.name?.toUpperCase() || "N/A"}</div>
                    <div style={styles.userDetails}>
                      <div style={styles.userDetailRow}>
                        <span style={styles.userDetailLabel}>Email: </span>
                        <span style={styles.userDetailValue}>{userItem.email || "-"}</span>
                      </div>
                      <div style={styles.userDetailRow}>
                        <span style={styles.userDetailLabel}>Role: </span>
                        <span style={{
                          ...styles.roleBadge,
                          backgroundColor: userItem.role === "admin" ? "#25d366" : "#667781",
                        }}>{userItem.role || "-"}</span>
                      </div>
                      {userItem.createdAt && (
                        <div style={styles.userDetailRow}>
                          <span style={styles.userDetailLabel}>Created: </span>
                          <span style={styles.userDetailValue}>
                            {new Date(userItem.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={styles.userActions}>
                    <PermissionGate resource="users" action="update">
                      <button
                        onClick={() => handleOpenModal(userItem)}
                        style={styles.userEditButton}
                        title="Edit User"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f2f5";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        ✏️
                      </button>
                    </PermissionGate>
                    <PermissionGate resource="users" action="delete">
                      <button
                        onClick={() => handleOpenDeleteModal(userItem._id)}
                        style={styles.userDeleteButton}
                        title="Delete User"
                        disabled={userItem._id === user?._id}
                        onMouseEnter={(e) => {
                          if (!e.currentTarget.disabled) {
                            e.currentTarget.style.backgroundColor = "#fff5f5";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        🗑️
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }} 
          onClick={handleCloseModal}
        >
          <div 
            style={{
              ...styles.modalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "20px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "500px",
              width: isMobile ? "100%" : "90%",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
              marginBottom: isMobile ? "100px" : "0",
              display: "flex",
              flexDirection: "column",
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              ...styles.modalHeader,
              marginBottom: isMobile ? "16px" : "24px",
            }}>
              <h3 style={{
                ...styles.modalTitle,
                fontSize: isMobile ? "20px" : "24px",
              }}>Edit User</h3>
              <button 
                onClick={handleCloseModal} 
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f2f5";
                  e.currentTarget.style.color = "#111b21";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#667781";
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{
              ...styles.form,
              gap: isMobile ? "16px" : "20px",
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.name ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter name"
                />
                {validationErrors.name && (
                  <span style={styles.error}>{validationErrors.name}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Email <span style={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.email ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter email"
                />
                {validationErrors.email && (
                  <span style={styles.error}>{validationErrors.email}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Role <span style={styles.required}>*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.role ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                >
                  <option value="">Select Role</option>
                  {activeRoles
                    .filter((role) => role.isActive)
                    .map((role) => (
                      <option key={role._id} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                </select>
                {validationErrors.role && (
                  <span style={styles.error}>{validationErrors.role}</span>
                )}
              </div>

              <div style={{
                ...styles.formActions,
                marginTop: isMobile ? "8px" : "16px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    ...styles.cancelButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "100px",
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.submitButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "140px",
                  }}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }} 
          onClick={handleCloseDeleteModal}
        >
          <div
            style={{
              ...styles.deleteModalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "24px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "400px",
              width: isMobile ? "100%" : "90%",
              marginBottom: isMobile ? "100px" : "0",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              ...styles.deleteModalTitle,
              fontSize: isMobile ? "18px" : "20px",
            }}>Confirm Delete</h3>
            <p style={styles.deleteModalText}>
              Are you sure you want to delete this user? This action cannot be
              undone.
            </p>
            <div style={{
              ...styles.deleteModalActions,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  ...styles.cancelButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete} 
                style={{
                  ...styles.deleteConfirmButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "24px",
    paddingBottom: "100px",
    minHeight: "calc(100vh - 80px)",
  },
  pageHeader: {
    marginBottom: "24px",
    paddingBottom: "16px",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  searchContainer: {
    marginBottom: "20px",
    padding: "12px",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px 12px 44px",
    border: "1px solid #e9edef",
    borderRadius: "24px",
    backgroundColor: "#ffffff",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    outline: "none",
    transition: "border-color 0.2s",
    letterSpacing: "-0.01em",
  },
  usersContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "20px",
  },
  userCard: {
    backgroundColor: "#ffeef2",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #ffd6e0",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  userCardContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#111b21",
    marginBottom: "8px",
    letterSpacing: "-0.01em",
    lineHeight: "22px",
    textTransform: "uppercase",
  },
  userDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  userDetailRow: {
    fontSize: "14px",
    fontWeight: "400",
    color: "#667781",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  userDetailLabel: {
    fontWeight: "500",
    color: "#667781",
    flexShrink: 0,
  },
  userDetailValue: {
    color: "#111b21",
  },
  roleBadge: {
    padding: "4px 10px",
    borderRadius: "12px",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "500",
    display: "inline-block",
  },
  userActions: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
  },
  userEditButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  userDeleteButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    opacity: 1,
  },
  noUsers: {
    padding: "80px 40px",
    textAlign: "center",
    color: "#667781",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    display: "flex",
    justifyContent: "center",
    zIndex: 1002,
    overflowY: "auto",
    paddingBottom: "100px",
  },
  modalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "30px",
    maxWidth: "500px",
    width: "90%",
    maxHeight: "calc(90vh - 100px)",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e9edef",
  },
  modalTitle: {
    fontSize: "24px",
    fontWeight: "500",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  closeButton: {
    backgroundColor: "transparent",
    border: "none",
    fontSize: "32px",
    cursor: "pointer",
    color: "#667781",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, color 0.2s",
    lineHeight: "1",
    fontWeight: "300",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  input: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
  },
  error: {
    color: "#dc3545",
    fontSize: "12px",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
    borderTop: "1px solid #e9edef",
    marginTop: "auto",
    position: "sticky",
    bottom: 0,
    backgroundColor: "#ffffff",
    zIndex: 1,
  },
  cancelButton: {
    padding: "12px 24px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
  },
  submitButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "140px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  deleteModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "30px",
    maxWidth: "400px",
    width: "90%",
    maxHeight: "calc(90vh - 100px)",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
  },
  deleteModalTitle: {
    fontSize: "20px",
    fontWeight: "500",
    color: "#111b21",
    marginBottom: "12px",
    letterSpacing: "-0.01em",
  },
  deleteModalText: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "24px",
    lineHeight: "20px",
    fontWeight: "400",
  },
  deleteModalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
  },
  deleteConfirmButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
};

export default UserManagement;

